import { z } from 'zod';
import { ATTR_KEYS, ELO_START, RATING_STATES, TIER_IDS } from '../core/ratings';

/** Frozen rating schemas (docs/design/add-ratings.md "Foundation additions"). */

export const AttrKeyZ = z.enum(ATTR_KEYS);
export const RatingStateZ = z.enum(RATING_STATES);
export const TierIdZ = z.enum(TIER_IDS);

const Rating = z.number().int().min(0).max(99);

export const AttrCellZ = z.object({
  value: Rating.nullable(),
  conf: z.number().min(0).max(1),
  cold: z.boolean(),
});
export type AttrCell = z.infer<typeof AttrCellZ>;

export const RatingCardZ = z.object({
  playerId: z.string(),
  state: RatingStateZ,
  ovr: Rating.nullable(),
  tier: TierIdZ.nullable(),
  attrs: z.record(AttrKeyZ, AttrCellZ),
  archetype: z.string().nullable(),
  confidence: z.number().min(0).max(1),
  eloBlend: z.number(),
  ratedGames: z.number().int().nonnegative(),
  capped: z.boolean(),
  /** The instant this card was computed. The nightly job recomputes cards as of now, so `rusty` and decay stay fresh. */
  asOf: z.number().int(),
  /**
   * When the newest evidence behind the rating happened (a counted game or drill shot), null when
   * there is none. The rusty subline "Last bucket: {n} days ago." reads it; `asOf` cannot serve,
   * because the nightly refresh rewrites it.
   */
  lastEvidenceAt: z.number().int().nullable(),
  engineVersion: z.number().int(),
});
export type RatingCard = z.infer<typeof RatingCardZ>;

/**
 * The card of a player with no evidence: state unrated, OVR '??', every attribute cold. Stubs and
 * loading states use it so consumers always get a full RatingCard (the stats module replaces the
 * real values; `engineVersion` 0 marks a card the engine has not produced).
 */
export function unratedCard(playerId: string, asOf = 0): RatingCard {
  const attrs = Object.fromEntries(
    ATTR_KEYS.map((k) => [k, { value: null, conf: 0, cold: true }]),
  ) as RatingCard['attrs'];
  return {
    playerId,
    state: 'unrated',
    ovr: null,
    tier: null,
    attrs,
    archetype: null,
    confidence: 0,
    eloBlend: ELO_START,
    ratedGames: 0,
    capped: false,
    asOf,
    lastEvidenceAt: null,
    engineVersion: 0,
  };
}

export const DeltaSourceZ = z.object({
  kind: z.enum(['game', 'drill_session']),
  id: z.string(),
});
export type DeltaSource = z.infer<typeof DeltaSourceZ>;

export const RatingDeltaZ = z.object({
  playerId: z.string(),
  source: DeltaSourceZ,
  ovr: z.object({
    before: z.number().int().nullable(),
    after: z.number().int().nullable(),
    delta: z.number().int(),
  }),
  tier: z.object({
    before: TierIdZ.nullable(),
    after: TierIdZ.nullable(),
    crossed: z.enum(['up', 'down']).nullable(),
  }),
  state: z.object({ before: RatingStateZ, after: RatingStateZ }),
  attrs: z.array(
    z.object({
      key: AttrKeyZ,
      before: z.number().int(),
      after: z.number().int(),
      delta: z.number().int(),
    }),
  ),
  elo: z
    .array(
      z.object({
        kind: z.enum(['1v1', 'team']),
        before: z.number(),
        after: z.number(),
      }),
    )
    .optional(),
});
export type RatingDelta = z.infer<typeof RatingDeltaZ>;

export const RatingHistoryZ = z.object({
  playerId: z.string(),
  points: z.array(
    z.object({
      asOf: z.number().int(),
      ovr: z.number().int().nullable(),
      state: RatingStateZ,
      tier: TierIdZ.nullable(),
      attrs: z.record(AttrKeyZ, z.number().int().nullable()),
    }),
  ),
});
export type RatingHistory = z.infer<typeof RatingHistoryZ>;
